import type {
  AnalysisRequest,
  AnalysisResponse,
  ComparisonRequest,
  ComparisonSet,
  CreateDecisionInput,
  CreateSavedPanelInput,
  DecisionAction,
  DecisionDto,
  PathologyPreset,
  SavedPanel,
  UpdateDecisionInput,
  UpdateSavedPanelInput,
} from "@shared/schema";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw Object.assign(new Error(`Request failed ${res.status}`), {
      status: res.status,
      body,
    });
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  runAnalysis(body: AnalysisRequest) {
    return request<AnalysisResponse>("/analysis/run", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  runComparison(body: ComparisonRequest) {
    return request<ComparisonSet>("/analysis/compare", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  downloadCsvUrl(): string {
    return "/api/exports/csv";
  },
  downloadJsonUrl(): string {
    return "/api/exports/json";
  },
  downloadComparisonCsvUrl(): string {
    return "/api/exports/comparison/csv";
  },
  downloadComparisonJsonUrl(): string {
    return "/api/exports/comparison/json";
  },
  listPanels() {
    return request<SavedPanel[]>("/panels");
  },
  createPanel(input: CreateSavedPanelInput) {
    return request<SavedPanel>("/panels", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updatePanel(id: number, input: UpdateSavedPanelInput) {
    return request<SavedPanel>(`/panels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  deletePanel(id: number) {
    return request<void>(`/panels/${id}`, { method: "DELETE" });
  },
  listTissues() {
    return request<Array<{ id: string; label: string; shortLabel: string; geneCount: number }>>(
      "/contexts/tissues",
    );
  },
  listCellTypes(tissueId: string) {
    return request<Array<{ id: string; label: string; markerGeneCount: number }>>(
      `/contexts/tissues/${encodeURIComponent(tissueId)}/cell-types`,
    );
  },
  listDiseaseModes() {
    return request<Array<{ id: string; label: string; description?: string }>>(
      "/contexts/disease-modes",
    );
  },
  listRepairStates() {
    return request<Array<{ id: string; label: string; description?: string }>>(
      "/contexts/repair-states",
    );
  },
  listPathologyPresets() {
    return request<{ version: string; presets: PathologyPreset[] }>("/presets/pathology");
  },

  listDecisions(params: {
    deviceId?: string;
    queryRunId?: string;
    interactionId?: string;
    actions?: DecisionAction[];
  } = {}) {
    const qs = new URLSearchParams();
    if (params.deviceId) qs.set("deviceId", params.deviceId);
    if (params.queryRunId) qs.set("queryRunId", params.queryRunId);
    if (params.interactionId) qs.set("interactionId", params.interactionId);
    if (params.actions && params.actions.length) qs.set("actions", params.actions.join(","));
    const suffix = qs.toString();
    return request<DecisionDto[]>(`/decisions${suffix ? `?${suffix}` : ""}`);
  },
  currentDecisionsForRun(deviceId: string, queryRunId: string) {
    const qs = new URLSearchParams({ mode: "currentForRun", deviceId, queryRunId });
    return request<Record<string, DecisionDto>>(`/decisions?${qs.toString()}`);
  },
  createDecision(input: CreateDecisionInput) {
    return request<DecisionDto>("/decisions", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateDecision(id: string, input: UpdateDecisionInput) {
    return request<DecisionDto>(`/decisions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  deleteDecision(id: string) {
    return request<void>(`/decisions/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};
